import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from 'date-fns';
import { api } from '../../../services/api';
import { formatDateOnlyNumeric } from '../../../utils/date';

type DayRecord = {
  date: string;
  rainfall_inches: number;
  temperature_f: number | null;
  conditions: string | null;
  wind_speed_mph: number | null;
  inspection_required: boolean;
};

type RainEvent = {
  date: string | null;
  inspection_id: string;
  status: string;
  scheduled_date: string | null;
  conducted_date: string | null;
  /** Jim 2026-10-01: he wants the calendar to show that the alerts go out,
   * and when. Older events predate the alert payload, hence the nulls. */
  triggered_at?: string | null;
  rainfall_inches?: number | null;
  threshold_inches?: number | null;
  alert_sent_at?: string | null;
  alert_recipients?: string[];
  alert_channels?: string[];
};

const CHANNEL_WORD: Record<string, string> = {
  email: 'emailed',
  in_app: 'in the app',
  sms: 'texted',
};

/** "emailed and in the app", from whatever channels actually went out. */
export function describeChannels(channels: string[] | undefined): string {
  const words = (channels ?? []).map((c) => CHANNEL_WORD[c] ?? c);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0];
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** One line per rain event: what fell, when the alert went out, to whom. */
export function describeRainEvent(ev: RainEvent): string {
  const fell =
    ev.rainfall_inches != null
      ? `${ev.rainfall_inches}" fell${ev.threshold_inches != null ? ` (over ${ev.threshold_inches}")` : ''}`
      : 'Rain event';
  const sent = ev.alert_sent_at
    ? `alert ${describeChannels(ev.alert_channels) || 'sent'} ${format(parseISO(ev.alert_sent_at), 'h:mm a')}${
        ev.alert_recipients?.length
          ? ` to ${ev.alert_recipients.join(', ')}`
          : ''
      }`
    : 'no alert recorded';
  const due =
    ev.status === 'completed' || ev.conducted_date
      ? `inspected ${formatDateOnlyNumeric(ev.conducted_date) || 'done'}`
      : ev.scheduled_date
        ? `inspection due ${formatDateOnlyNumeric(ev.scheduled_date)}`
        : ev.status;
  return `${fell} · ${sent} · ${due}`;
}

type MonthResponse = {
  status: string;
  data: {
    month: string;
    days: DayRecord[];
    days_with_data: number;
    total_rainfall_inches: number;
    rain_events?: RainEvent[];
    first_recorded_month: string | null;
  };
};

/** The seven days ending today, oldest first — what the phone shows. */
export function lastSevenDays(today: Date): Date[] {
  return eachDayOfInterval({ start: subDays(today, 6), end: today });
}

/** The `yyyy-MM` months a run of days touches (one, or two across a month end). */
export function monthsCovering(days: Date[]): string[] {
  return [...new Set(days.map((d) => format(d, 'yyyy-MM')))];
}

/**
 * Recorded weather and rain-event alerts for a site, in two shapes.
 *
 * `month` (the Hub, for admins and consultants) is the browsable calendar
 * Jim asked for on 8/9: "go back and look at what history we have
 * recorded... flipped thru like a calendar". Back-navigation is bounded by
 * the first recorded month, forward by the current month.
 *
 * `week` (EnviroLite) is the last seven days only — Chris 2026-10-03: a
 * full month is too much on a phone; the inspector needs this week's rain
 * and alerts, the history belongs on the Hub.
 */
export function WeatherHistoryCalendar({
  siteId,
  view = 'month',
}: {
  siteId: string;
  view?: 'month' | 'week';
}) {
  const [month, setMonth] = useState(() => format(new Date(), 'yyyy-MM'));
  const isWeek = view === 'week';
  const weekDays = lastSevenDays(new Date());
  const months = isWeek ? monthsCovering(weekDays) : [month];

  const results = useQueries({
    queries: months.map((m) => ({
      queryKey: ['weather-month', siteId, m],
      queryFn: async () =>
        (
          await api.get<MonthResponse>(
            `/weather/site/${siteId}/history/month?month=${m}`
          )
        ).data.data,
      enabled: !!siteId,
      staleTime: 15 * 60 * 1000,
    })),
  });
  const isLoading = results.some((r) => r.isLoading);
  const loaded = results.flatMap((r) => (r.data ? [r.data] : []));
  // Month view reads its single month; week view never shows these totals.
  const data = isWeek ? undefined : loaded[0];

  const monthDate = parseISO(`${month}-01`);
  const currentMonth = format(new Date(), 'yyyy-MM');
  const firstMonth = data?.first_recorded_month ?? null;
  const canBack = !firstMonth || month > firstMonth;
  const canFwd = month < currentMonth;

  const byDate = new Map(loaded.flatMap((m) => m.days).map((d) => [d.date, d]));
  const allEvents = loaded.flatMap((m) => m.rain_events ?? []);
  const eventsByDate = new Map<string, RainEvent>();
  for (const ev of allEvents) {
    if (ev.date && !eventsByDate.has(ev.date)) eventsByDate.set(ev.date, ev);
  }
  const days = isWeek
    ? weekDays
    : eachDayOfInterval({
        start: startOfWeek(startOfMonth(monthDate), { weekStartsOn: 0 }),
        end: endOfMonth(monthDate),
      });
  const shownKeys = new Set(days.map((d) => format(d, 'yyyy-MM-dd')));
  // Newest first: a storm the inspector is being told about right now.
  const listedEvents = allEvents
    .filter((ev) => ev.date && (!isWeek || shownKeys.has(ev.date)))
    .sort((a, b) => (a.date! < b.date! ? 1 : -1));
  const weekRain = weekDays.reduce(
    (sum, d) =>
      sum + (byDate.get(format(d, 'yyyy-MM-dd'))?.rainfall_inches ?? 0),
    0
  );

  return (
    <div className="p-3">
      {isWeek ? (
        <div className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-200">
          Last 7 days
          {!isLoading && (
            <span className="ml-2 font-normal text-xs text-slate-500 dark:text-slate-400">
              {Number(weekRain.toFixed(2))}" rain
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            disabled={!canBack}
            onClick={() => setMonth(format(subMonths(monthDate, 1), 'yyyy-MM'))}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">
            {format(monthDate, 'MMMM yyyy')}
            {data && (
              <span className="ml-2 font-normal text-xs text-slate-500 dark:text-slate-400">
                {data.total_rainfall_inches}" rain · {data.days_with_data} days
                recorded
              </span>
            )}
          </div>
          <button
            type="button"
            disabled={!canFwd}
            onClick={() => setMonth(format(addMonths(monthDate, 1), 'yyyy-MM'))}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30"
            aria-label="Next month"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-6 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-medium text-slate-400 dark:text-slate-500 mb-1">
            {(isWeek
              ? weekDays.map((d) => format(d, 'EEEEE'))
              : ['S', 'M', 'T', 'W', 'T', 'F', 'S']
            ).map((d, i) => (
              <div key={i}>{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const rec = byDate.get(key);
              const event = eventsByDate.get(key);
              const inMonth = isWeek || format(day, 'yyyy-MM') === month;
              const eventTip = event ? ` · ${describeRainEvent(event)}` : '';
              return (
                <div
                  key={key}
                  className={`rounded-lg px-0.5 py-1 text-center min-h-[44px] ${
                    !inMonth
                      ? 'opacity-0 pointer-events-none'
                      : rec?.inspection_required
                        ? 'bg-blue-100 dark:bg-blue-900/40 ring-1 ring-blue-400'
                        : rec
                          ? 'bg-slate-50 dark:bg-slate-700/40'
                          : 'bg-transparent'
                  }`}
                  title={
                    rec
                      ? `${rec.rainfall_inches}" rain${rec.temperature_f != null ? ` · ${Math.round(rec.temperature_f)}°F` : ''}${rec.inspection_required ? ' · rain-event inspection required' : ''}${eventTip}`
                      : event
                        ? `Rain event${eventTip.slice(2)}`
                        : 'No data recorded'
                  }
                >
                  <div
                    className={`text-[11px] ${rec ? 'font-medium text-slate-700 dark:text-slate-200' : 'text-slate-300 dark:text-slate-600'}`}
                  >
                    {format(day, 'd')}
                  </div>
                  {event && (
                    <div
                      className={`mx-auto mt-0.5 w-1.5 h-1.5 rounded-full ${
                        event.status === 'completed'
                          ? 'bg-emerald-500'
                          : 'bg-amber-500'
                      }`}
                    />
                  )}
                  {rec && (
                    <div
                      className={`text-[10px] leading-tight ${rec.rainfall_inches > 0 ? 'text-blue-600 dark:text-blue-400 font-semibold' : 'text-slate-400 dark:text-slate-500'}`}
                    >
                      {rec.rainfall_inches > 0
                        ? `${rec.rainfall_inches}"`
                        : '—'}
                      {rec.temperature_f != null && (
                        <div className="text-slate-500 dark:text-slate-400 font-normal">
                          {Math.round(rec.temperature_f)}°
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-[10px] text-slate-400 dark:text-slate-500">
            Blue-ringed days crossed the site's rain-event threshold. A dot
            marks a triggered rain-event inspection — amber while pending, green
            once completed.
          </p>
          {listedEvents.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                {isWeek
                  ? 'Rain events in the last 7 days'
                  : 'Rain events this month'}
              </p>
              <ul className="space-y-1.5" data-testid="rain-event-log">
                {listedEvents.map((ev) => (
                  <li key={ev.inspection_id} className="flex items-start gap-2">
                    <span
                      className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        ev.status === 'completed'
                          ? 'bg-emerald-500'
                          : 'bg-amber-500'
                      }`}
                      aria-hidden="true"
                    />
                    <span className="text-[11px] leading-snug text-slate-600 dark:text-slate-300">
                      <span className="font-medium text-slate-800 dark:text-slate-100">
                        {formatDateOnlyNumeric(ev.date)}
                      </span>{' '}
                      — {describeRainEvent(ev)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
